import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
  uploadBusinessDocument,
  getBusinessDocuments,
  deleteBusinessDocument,
  openBusinessDocumentFile,
} from "../../../services/customer.service";
import { startupDocumentList } from "../../../constants/general.constant";
import { businessDocument } from "../../../constants/message.constant";
import Loader from "../../../common/loader";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";

const requiredDocs: string[] = startupDocumentList;

const docRequirements: Record<string, { min: number; max: number }> = {
  "Startup - New Project": { min: 1, max: 1 },
  "3 Year Projections": { min: 1, max: 3 },
  "Business Plan": { min: 1, max: 1 },
  "2-3 Page Whitepaper That Explains the Project": { min: 1, max: 3 },
};

const getCompactFileName = (fileName: string) =>
  fileName.length > 5 ? `${fileName.slice(0, 5)}...` : fileName;

interface UploadedDoc {
  id: string;
  docName: string;
  filePath: string;
  originalName: string;
}

const StartupDocumentsUpload: React.FC<{ customerId: string; customerBusinessId: string }> = ({
  customerId,
  customerBusinessId,
}) => {
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, File[]>>({});
  const [dragOverDoc, setDragOverDoc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [serverDocs, setServerDocs] = useState<UploadedDoc[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // delete dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<UploadedDoc | null>(null);
  const [previewingDocId, setPreviewingDocId] = useState<string | null>(null);

  const fetchServerDocs = async () => {
    try {
      const docs = await getBusinessDocuments(customerId, customerBusinessId);
      setServerDocs(docs || []);
    } catch {
      setServerDocs([]);
    }
  };

  useEffect(() => {
    fetchServerDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const MAX_FILE_SIZE_MB = 25;
  const handleFileChange = (docName: string, files: FileList | null) => {
    if (!files) return;
    const { max = 3 } = docRequirements[docName] || {};
    const existing = uploadedDocs[docName] || [];
    const incoming = Array.from(files);

    const serverFilesForDoc = serverDocs.filter((d) => d.docName === docName);
    const serverFileNames = serverFilesForDoc.map((d) =>
      d.originalName.toLowerCase()
    );

    for (const file of incoming) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: `${file.name} exceeds the ${MAX_FILE_SIZE_MB}MB limit`,
          severity: "error",
        });
        return;
      }

      const isDuplicate =
        existing.some(
          (f) => f.name.toLowerCase() === file.name.toLowerCase()
        ) || serverFileNames.includes(file.name.toLowerCase());

      if (isDuplicate) {
        setSnackbar({
          open: true,
          message: `File "${file.name}" has already been uploaded for ${docName}`,
          severity: "error",
        });
        return;
      }
    }

    if (existing.length + incoming.length > max) {
      setSnackbar({
        open: true,
        message: `You can only upload up to ${max} file(s) for ${docName}`,
        severity: "error",
      });
      return;
    }

    setUploadedDocs((prev) => ({
      ...prev,
      [docName]: [...existing, ...incoming],
    }));
  };

  const handleRemoveFile = (docName: string, index: number) => {
    setUploadedDocs((prev) => ({
      ...prev,
      [docName]: prev[docName].filter((_, i) => i !== index),
    }));
  };

  const handlePreviewFile = (file: File) => {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, "_blank");
  };

  const handlePreviewServerFile = async (docId: string) => {
    setPreviewingDocId(docId);
    try {
      await openBusinessDocumentFile(docId);
    } finally {
      setPreviewingDocId(null);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const allDocs = [
        ...Object.keys(uploadedDocs),
        ...serverDocs.map((d) => d.docName),
      ];
      const uniqueDocs = Array.from(new Set(allDocs));

      for (const docName of uniqueDocs) {
        const { min, max } = docRequirements[docName] || {};
        const localFiles = uploadedDocs[docName] || [];
        const serverFiles = serverDocs.filter((d) => d.docName === docName);

        const totalFiles = localFiles.length + serverFiles.length;

        if (min && totalFiles < min) {
          setSnackbar({
            open: true,
            message: `${docName} requires at least ${min} file(s)`,
            severity: "error",
          });
          setLoading(false);
          return;
        }
        if (max && totalFiles > max) {
          setSnackbar({
            open: true,
            message: `${docName} allows maximum ${max} file(s)`,
            severity: "error",
          });
          setLoading(false);
          return;
        }
      }

      for (const [docName, files] of Object.entries(uploadedDocs)) {
        for (const file of files) {
          await uploadBusinessDocument(customerId, customerBusinessId, docName, file);
        }
      }

      setUploadedDocs({});
      await fetchServerDocs();
      setSnackbar({
        open: true,
        message: businessDocument.createdDocument,
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || businessDocument.failedUpload,
        severity: "error",
      });
      setUploadedDocs({});
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDoc) return;
    try {
      setLoading(true);
      await deleteBusinessDocument(selectedDoc.id);
      setServerDocs((prev) => prev.filter((d) => d.id !== selectedDoc.id));
      setSnackbar({
        open: true,
        message: "Document deleted successfully",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to delete document",
        severity: "error",
      });
    } finally {
      setOpenDialog(false);
      setSelectedDoc(null);
      setLoading(false);
    }
  };

  const hasFiles = Object.values(uploadedDocs).some(
    (files) => files && files.length > 0
  );

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Card className="shadow-xl rounded-2xl border border-gray-200 bg-white">
          <CardContent className="p-4 sm:p-6">
            <Typography variant="h6" className="mb-4 sm:mb-6 font-bold text-gray-800 text-center sm:text-left">
              Startup Documents Upload
            </Typography>

            <Typography
              variant="body2"
              className="mb-4 sm:mb-6 text-red-600 font-medium text-center sm:text-left"
            >
              ⚠️ Maximum file size allowed: 25MB (images or PDFs only)
            </Typography>

            <div className="space-y-4">
              {requiredDocs.map((docName) => {
                const files = uploadedDocs[docName] || [];

                const groupedServerDocs = serverDocs && serverDocs.reduce((acc, doc) => {
                  if (!acc[doc.docName]) acc[doc.docName] = [];
                  acc[doc.docName].push(doc);
                  return acc;
                }, {} as Record<string, UploadedDoc[]>);

                const serverDocsForThis = groupedServerDocs[docName] || [];

                const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  setDragOverDoc(null);
                  if (e.dataTransfer.files?.length) {
                    handleFileChange(docName, e.dataTransfer.files);
                  }
                };

                const { max = 3 } = docRequirements[docName] || {};

                return (
                  <div
                    key={docName}
                    className="flex flex-col gap-3 p-3 sm:p-4 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    {/* Top: status + label */}
                    <div className="flex items-center space-x-2">
                      {Array.from({
                        length: docRequirements[docName]?.max || 1,
                      }).map((_, i) => {
                        const totalUploaded =
                          (files.length || 0) + (serverDocsForThis.length || 0);
                        return i < totalUploaded ? (
                          <CheckCircleIcon
                            key={docName + i}
                            className="text-green-500"
                            fontSize="small"
                          />
                        ) : (
                          <HourglassEmptyIcon
                            key={docName + i}
                            className="text-gray-400"
                            fontSize="small"
                          />
                        );
                      })}
                      <Typography className="text-sm font-medium text-gray-700">
                        {docName}
                      </Typography>
                    </div>

                    {/* Bottom: actions */}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      className="gap-2"
                      sx={{ width: '100%', justifyContent: { xs: 'center', sm: 'flex-start' } }}
                    >
                      {/* Server docs */}
                      {serverDocsForThis.map((doc) => (
                        <Stack
                          key={doc.id}
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <Tooltip title={doc.originalName}>
                            <Chip
                              label={
                                <span className="block max-w-[64px] truncate">
                                  {getCompactFileName(doc.originalName)}
                                </span>
                              }
                              size="small"
                              onDelete={() => {
                                setSelectedDoc(doc);
                                setOpenDialog(true);
                              }}
                              deleteIcon={<DeleteIcon fontSize="small" />}
                              className="bg-green-100 text-green-700 font-medium"
                            />
                          </Tooltip>
                          <Tooltip title="Preview File">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={previewingDocId === doc.id}
                              onClick={() =>
                                handlePreviewServerFile(doc.id)
                              }
                            >
                              {previewingDocId === doc.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ))}

                      {/* Local files before save */}
                      {files.map((file, index) => (
                        <Stack
                          key={file.name + index}
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <Tooltip title={file.name}>
                            <Chip
                              label={
                                <span className="block max-w-[64px] truncate">
                                  {getCompactFileName(file.name)}
                                </span>
                              }
                              size="small"
                              onDelete={() => handleRemoveFile(docName, index)}
                              deleteIcon={<DeleteIcon fontSize="small" />}
                              className="bg-blue-100 text-blue-700 font-medium"
                            />
                          </Tooltip>
                          <Tooltip title="Preview File">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handlePreviewFile(file)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ))}

                      {/* Upload button */}
                      <button
                        type="button"
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOverDoc(docName);
                        }}
                        onDragLeave={() => setDragOverDoc(null)}
                        onDrop={handleDrop}
                        className={`border-2 border-dashed rounded-lg px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-center cursor-pointer transition w-full sm:w-auto ${
                          dragOverDoc === docName
                            ? "border-blue-600 bg-blue-50"
                            : "border-blue-500 bg-white hover:bg-blue-50"
                        }`}
                      >
                        <label className="flex items-center gap-1 sm:gap-2 cursor-pointer text-xs sm:text-sm font-medium text-blue-600">
                          <UploadFileIcon color="primary" fontSize="small" />
                          <span className="text-center">
                            {max > 1
                              ? `Upload up to ${max} Documents`
                              : "Upload Document"}
                          </span>
                          <input
                            id={`file-input-${docName}`}
                            type="file"
                            hidden
                            accept="image/*,application/pdf"
                            multiple={max > 1}
                            onChange={(e) =>
                              handleFileChange(docName, e.target.files)
                            }
                          />
                        </label>
                      </button>
                    </Stack>
                  </div>
                );
              })}
            </div>

            {/* Save */}
            <div className="flex justify-center sm:justify-end mt-4 sm:mt-6">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={!hasFiles || loading}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  width: { xs: '100%', sm: 'auto' },
                  py: { xs: 1.5, sm: 1 }
                }}
                startIcon={!loading ? <SaveIcon /> : null}
              >
                {loading ? "Saving..." : "Save Documents"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirm Delete Dialog */}
      <ConfirmDeleteDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleDeleteConfirm}
        itemName={selectedDoc?.originalName || ""}
      />
    </>
  );
};

export default StartupDocumentsUpload;

